import { useUsers } from '@/api/useUsers'
import type { components } from '@/types/api/v1'
import { GraviButton, Horizontal, Texto } from '@gravitate-js/excalibrr'
import { Form, Input, Modal, notification } from 'antd'
import type React from 'react'

interface PasswordChangeModalProps {
  isVisible: boolean
  setIsVisible: (visible: boolean) => void
  driver?: components['schemas']['DriverInfoRow'] | null
}

type FormValues = { password: string; confirmPassword: string }

export const PasswordChangeModal: React.FC<PasswordChangeModalProps> = ({ isVisible, setIsVisible, driver }) => {
  const [form] = Form.useForm<FormValues>()
  const { updateDriverPWMutation: changePasswordMutation } = useUsers()
  const handleCancel = () => {
    form.resetFields()
    setIsVisible(false)
  }

  const handleSubmit = async (values: FormValues) => {
    if (!driver) return

    if (values.password !== values.confirmPassword) {
      notification.error({
        message: 'Error',
        description: 'Passwords do not match',
      })
      return
    }

    try {
      const payload = {
        username: driver.username,
        new_password: values.password,
      }
      // biome-ignore lint/suspicious/noExplicitAny: weird void arg type returned by mutation definition
      await changePasswordMutation.mutateAsync(payload as unknown as any)

      notification.success({
        message: 'Success',
        description: `Password successfully changed for ${driver.name}`,
      })
      form.resetFields()
      setIsVisible(false)
    } catch (error) {
      console.error('Error changing password:', error)
      notification.error({
        message: 'Error',
        description: 'Failed to change password',
      })
    }
  }

  return (
    <Modal
      title={<Texto category='heading'>Change Driver Password</Texto>}
      open={isVisible}
      onCancel={handleCancel}
      footer={null}
      destroyOnClose
    >
      <Form form={form} layout='vertical' onFinish={handleSubmit}>
        <Form.Item
          name='password'
          label='New Password'
          rules={[{ required: true, message: 'Please enter a new password' }]}
        >
          <Input.Password />
        </Form.Item>

        <Form.Item
          name='confirmPassword'
          label='Confirm Password'
          rules={[
            { required: true, message: 'Please confirm the password' },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('password') === value) {
                  return Promise.resolve()
                }
                return Promise.reject(new Error('The two passwords do not match'))
              },
            }),
          ]}
        >
          <Input.Password />
        </Form.Item>

        <Horizontal justifyContent='flex-end' gap={16}>
          <GraviButton buttonText='Cancel' onClick={handleCancel} />
          <GraviButton
            buttonText='Change Password'
            theme2
            loading={changePasswordMutation.isPending}
            onClick={form.submit}
            data-cy='confirm-password-change-btn'
          />
        </Horizontal>
      </Form>
    </Modal>
  )
}
